import type { RouteFactory } from '@/routing/types'
import { Outlet, redirect } from 'react-router-dom'
import { TimelineAssetRouteSegments, TimelineAssetTabs } from '@/timeline/asset/constants'
import AssetEventsTable from '@/matter-asset-common/events/AssetEventsTable'
import TimelineAssetRoot from '@/timeline/asset/components/TimelineAssetRoot'
import TimelineAssetLayout from '@/timeline/asset/components/TimelineAssetLayout'
import AddFilesPage from '@/timeline/asset/components/add-files/AddFilesPage'
import SourceFilesTab from '@/timeline/asset/components/source-files/SourceFilesTab'
import BrandAccessGuard from '@/components/guards/brand-access-guard'
import { BrandEnum } from '@/firebase/auth/auth-jwt-schema'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'

/**
 * Creates timeline routes for nesting under MatterLayout.
 * URL pattern: /dashboard/m/:matterId/timeline/...
 *
 * The living timeline rides the new user-files pipeline: every ingestion path is user_file-id
 * based (uploads via useDriveUpload, user_file_ids in the asset API payloads) with no legacy GCS
 * fallback, so without NEW_FILES_UX the feature's data sources do not exist and the routes are not
 * registered at all. The HAIKU brand does not have the pipeline either — gate the routes like
 * med-chron/billing-summaries (the sidenav hides the entry behind the same two checks).
 *
 * The index is the matter's living timeline (one per matter). The legacy job viewer is gone
 * (hard cutover); old job deep links land on the living timeline.
 *
 * @see MatterLayout for parent route context
 */
export const createTimelineChildRoutes: RouteFactory = () => (kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX) ? createBrandGatedTimelineRoutes() : [])

const createBrandGatedTimelineRoutes: RouteFactory = () => [
  {
    path: 'timeline',
    element: <BrandAccessGuard excludedBrands={[BrandEnum.HAIKU]} />,
    children: [
      {
        element: (
          <TimelineAssetRoot>
            <Outlet />
          </TimelineAssetRoot>
        ),
        children: [
          // The living timeline (tabbed shell). Timeline has a single events view, so the All Events
          // tab mounts the shared AssetEventsTable directly (no variant); columns come from
          // timelineFeatureConfig via the shell's MatterAssetFeatureProvider.
          {
            element: <TimelineAssetLayout />,
            children: [
              { index: true, loader: () => redirect(TimelineAssetTabs.ALL_EVENTS) },
              { path: TimelineAssetTabs.ALL_EVENTS, element: <AssetEventsTable /> },
              { path: TimelineAssetTabs.SOURCE_FILES, element: <SourceFilesTab /> },
            ],
          },
          // Stage files and generate/update the timeline
          { path: TimelineAssetRouteSegments.ADD_FILES, element: <AddFilesPage /> },
          // Hard cutover: legacy job deep links land on the living timeline
          { path: '*', loader: () => redirect('..') },
        ],
      },
    ],
  },
]
